import { formatAppVersion, isNewerAppVersion, parseAppVersion } from "@/lib/appVersion";

/**
 * `/version.json` is written by the build from `VITE_APP_VERSION` and served
 * `no-store` by nginx (#917): `{"version": "26.9.62", "min_version": null}`.
 */
export const VERSION_JSON_PATH = "/version.json";

export interface VersionInfo {
  version: unknown;
  minVersion: unknown;
}

/** One `no-store` read of `/version.json`; null on any network, HTTP or JSON failure. */
export const fetchVersionInfo = async (
  fetchImpl: typeof fetch = fetch
): Promise<VersionInfo | null> => {
  try {
    const response = await fetchImpl(VERSION_JSON_PATH, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return null;
    }
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null) {
      return null;
    }
    const record = body as Record<string, unknown>;
    return { version: record.version, minVersion: record.min_version };
  } catch {
    return null;
  }
};

export interface VersionObservation {
  /** `min_version` is newer than the running bundle: it must reload now. */
  reloadRequired: boolean;
  /** A newer release seen on two consecutive checks, else null. */
  newVersion: string | null;
}

export interface VersionTracker {
  observe(info: VersionInfo): VersionObservation;
}

/**
 * Decides when a newer release is real. During a rollout old and new pods
 * answer side by side, so one sighting proves nothing: the SAME newer version
 * must come back on two consecutive checks. Any check that sees the running
 * version (or older) resets the count, so an alternating old/new rollout never
 * confirms. Once confirmed it stays confirmed: the tab is outdated either way.
 */
export const createVersionTracker = (runningVersion: string): VersionTracker => {
  let candidate: string | null = null;
  let confirmed: string | null = null;

  return {
    observe({ version, minVersion }) {
      const reloadRequired = isNewerAppVersion(minVersion, runningVersion);
      if (confirmed === null) {
        const parsed = parseAppVersion(version);
        if (parsed !== null && isNewerAppVersion(version, runningVersion)) {
          const seen = formatAppVersion(parsed);
          if (seen === candidate) {
            confirmed = seen;
          }
          candidate = seen;
        } else {
          candidate = null;
        }
      }
      return { reloadRequired, newVersion: confirmed };
    },
  };
};
